import React, { useState, useEffect, useMemo } from 'react';
import IconItem from 'components/common/icon/IconItem';
import IconButton from 'components/common/IconButton';
import axios from 'helpers/api';
import AdvanceTableFooter from 'components/common/advance-table/AdvanceTableFooter';
import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import AdvanceTableSearchBox from 'components/common/advance-table/AdvanceTableSearchBox';
import { Badge, Col, Form, Row } from 'react-bootstrap';
import { toast } from 'react-toastify';

const MedRecordCategoryTable = (props) => {
    const [medrecordcategory, setMedRecordCategory] = useState([]);
    const [statusFilter, setStatusFilter] = useState('active');

    function handleEditInput(original) {
        props.onCallEditParent(original);
    }

    const columns = useMemo(() => [
        {
            accessorKey: 'code',
            header: 'Code',
            meta: {
                headerProps: { className: 'text-center text-900' },
                cellProps: { className: 'text-center align-middle' }
            }
        },
        {
            accessorKey: 'description',
            header: 'Description',
            meta: {
                headerProps: { className: 'text-center text-900' },
                cellProps: { className: 'text-center align-middle' }
            }
        },
        {
            accessorKey: 'mar_flag',
            header: 'Medsheet',
            meta: {
                headerProps: { className: 'text-center text-900' },
                cellProps: { className: 'text-center align-middle' }
            }
        },
        {
            accessorKey: 'tar_flag',
            header: 'Treatment',
            meta: {
                headerProps: { className: 'text-center text-900' },
                cellProps: { className: 'text-center align-middle' }
            }
        },
        {
            accessorKey: 'po_flag',
            header: 'Physician Order',
            meta: {
                headerProps: { className: 'text-center text-900' },
                cellProps: { className: 'text-center align-middle' }
            }
        },
        {
            accessorKey: 'is_active',
            header: 'Status',
            meta: {
                headerProps: { className: 'text-900 text-center' },
                cellProps: { className: 'text-center' }
            },
            cell: ({ row: { original } }) => {
                const isActive = original.is_active == 1 || original.is_active === '1' || original.is_active === true;
                return (
                    <Badge bg={isActive ? 'success' : 'secondary'}>
                        {isActive ? 'Active' : 'Inactive'}
                    </Badge>
                );
            }
        },
        {
            accessorKey: 'Action',
            header: 'Action',
            meta: {
                headerProps: { className: 'text-900 text-center', style: { width: '100px' } },
                cellProps: { className: 'text-center align-middle', style: { width: '100px' } }
            },
            enableSorting: false,
            cell: ({ row: { original } }) => {
                return (
                    <div className="d-flex justify-content-center align-items-center">
                        <IconItem
                            tag="button"
                            icon={['far', 'edit']}
                            size="sm"
                            onClick={() => handleEditInput(original)}
                            className="btn rounded-3 fs-11 align-items-center"
                        />
                    </div>
                );
            }
        }
    ], []);

    useEffect(() => {
        async function fetchMedRecCatDetails() {
            let tokenStr = localStorage.getItem("accessToken");
            try {
                const { data } = await axios.get(`${process.env.REACT_APP_SERVER_URL}/patients/list-medical-record-category`, {
                    headers: { 'Authorization': `Bearer ${tokenStr}` }
                });

                if (data.status && data.data) {
                    const results = data.data.map(value => ({
                        code: value.code,
                        description: value.description,
                        mar_flag: Number(value.mar_flag) ? 'Y' : 'N',
                        tar_flag: Number(value.tar_flag) ? 'Y' : 'N',
                        po_flag: Number(value.po_flag) ? 'Y' : 'N',
                        mc_id: value.id,
                        id: value.id,
                        medical_record_category: value.category || value.medical_record_category,
                        category_id: value.id,
                        is_active: value.is_active
                    }));
                    setMedRecordCategory(results);
                } else {
                    setMedRecordCategory([]);
                }
            } catch (err) {
                toast.info("No Medical Record Category found in this list");
                setMedRecordCategory([]);
            }
        }

        fetchMedRecCatDetails();
    }, [props.trigger]);

    const filteredData = useMemo(() => {
        return medrecordcategory.filter(item => {
            const isActive = item.is_active == 1 || item.is_active === '1' || item.is_active === true;
            if (statusFilter === 'active') return isActive;
            if (statusFilter === 'inactive') return !isActive;
            return true;
        });
    }, [medrecordcategory, statusFilter]);

    const table = useAdvanceTable({
        data: filteredData,
        columns,
        selection: false,
        sortable: true,
        pagination: true
    });

    return (
        <div>
            <AdvanceTableProvider {...table}>
                <Row className="g-2 align-items-center mb-3">
                    <Col md={4}>
                        <AdvanceTableSearchBox placeholder="Search..." />
                    </Col>
                    <Col md={4} className="d-flex align-items-center gap-2">
                        <Form.Label className="mb-0 text-nowrap fw-semi-bold fs-10">Filter:</Form.Label>
                        <Form.Select
                            size="sm"
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            style={{ minWidth: '150px' }}
                        >
                            <option value="active">Active Medical Record Category</option>
                            <option value="inactive">Inactive Medical Record Category</option>
                            <option value="all">All Medical Record Category</option>
                        </Form.Select>
                    </Col>
                    <Col md={4} className="ms-auto d-flex justify-content-end">
                        <IconButton
                            variant="primary"
                            size="sm"
                            icon="plus"
                            transform="shrink-3 down-2"
                            onClick={props.onOpenAddModal}
                        >
                            Add Medical Record Category
                        </IconButton>
                    </Col>
                </Row>
                {filteredData.length > 0 ? (
                    <AdvanceTable
                        headerClassName="bg-200 text-nowrap align-middle text-center"
                        rowClassName="text-nowrap align-middle"
                        tableProps={{
                            size: 'lg',
                            striped: true,
                            className: 'fs-10 mb-0'
                        }}
                    />
                ) : (
                    <div className="d-flex justify-content-center align-items-center text-center py-4">
                        <p className="text-danger fw-bold mb-0">There is no data available in the table</p>
                    </div>
                )}
                <div className="mt-3">
                    <AdvanceTableFooter
                        rowInfo
                        navButtons
                        rowsPerPageSelection
                    />
                </div>
            </AdvanceTableProvider>
        </div>
    );
};

export default MedRecordCategoryTable;






